

import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Settings,
  FileText,
  HelpCircle,
  CheckCircle,
  Eye,
  Award,
  Timer,
  TrendingUp,
  Shield,
  Zap,
  Sparkles,
  X,
  Loader,
} from "lucide-react";
import {
  coursesApi,
  certificateExamApi,
  showSuccess,
  showError,
} from "../lib";
import { useAuth } from "../context/AuthContext";
// Types
interface Option {
  id: string;
  optionText: string;
}

interface Question {
  id: string;
  questionText: string;
  options: Option[];
  correctOptionId: string;
  points: number;
  explanation?: string;
  order?: number;
}

interface ExamFormData {
  courseId: string;
  title: string;
  description: string;
  passingScore: number;
  timeLimit: number;
  examDuration: number;
  attemptsAllowed: number;
  randomizeQuestions: boolean;
  showAnswersImmediately: boolean;
  requireProctoring: boolean;
  isActive: boolean;
  questions: Question[];
}

const generateId = () => Math.random().toString(36).substring(2, 15);

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.3 },
};

export default function CreateCertificateExamPage() {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId?: string }>();
  const { user } = useAuth();
  const [courses, setCourses] = useState<Array<{ _id: string; title: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingExam, setExistingExam] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<
    "details" | "questions" | "settings"
  >("details");
  const [showPreview, setShowPreview] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [formData, setFormData] = useState<ExamFormData>({
    courseId: courseId || "",
    title: "Certificate Exam",
    description: "",
    passingScore: 70,
    timeLimit: 60,
    examDuration: 120,
    attemptsAllowed: 3,
    randomizeQuestions: false,
    showAnswersImmediately: false,
    requireProctoring: false,
    isActive: true,
    questions: [],
  });

  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Check mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch courses and existing exam
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch courses for instructor using user ID
        let coursesData = [];

        if (user?._id) {
          try {
            // Get instructor's courses
            const coursesRes = await coursesApi.getInstructorCourses(user._id);
            if (coursesRes.success && coursesRes.data) {
              coursesData = coursesRes.data;
            }
          } catch (err) {
            console.log("Error fetching instructor courses:", err);
          }
        }

        // If no courses found, try to get all courses
        if (coursesData.length === 0) {
          try {
            const allCoursesRes = await coursesApi.getAllCourses({
              limit: 100,
            });
            if (allCoursesRes.success && allCoursesRes.data) {
              coursesData = allCoursesRes.data;
            }
          } catch (err) {
            console.log("Error fetching all courses:", err);
          }
        }

        setCourses(coursesData);

        // If courseId is provided, check for existing exam
        if (courseId) {
          try {
            const examRes =
              await certificateExamApi.getCertificateExamByCourse(courseId);
            if (examRes.success && examRes.data) {
              setExistingExam(examRes.data);

              // Populate form with existing exam data
              setFormData({
                courseId: examRes.data.course || courseId,
                title: examRes.data.title || "Certificate Exam",
                description: examRes.data.description || "",
                passingScore: examRes.data.passingScore || 70,
                timeLimit: examRes.data.timeLimit || 60,
                examDuration: examRes.data.examDuration || 120,
                attemptsAllowed: examRes.data.attemptsAllowed || 3,
                randomizeQuestions: examRes.data.randomizeQuestions || false,
                showAnswersImmediately:
                  examRes.data.showAnswersImmediately || false,
                requireProctoring: examRes.data.requireProctoring || false,
                isActive:
                  examRes.data.isActive !== undefined
                    ? examRes.data.isActive
                    : true,
                questions:
                  examRes.data.questions?.map((q: any) => ({
                    id: q.id || generateId(),
                    questionText: q.questionText,
                    options: q.options || [],
                    correctOptionId: q.correctOptionId,
                    points: q.points || 1,
                    explanation: q.explanation || "",
                    order: q.order,
                  })) || [],
              });
            }
          } catch (err) {
            console.log("No existing exam found for this course");
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        showError("Failed to load courses. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [courseId, user?._id]);

  const handleAddQuestion = () => {
    setCurrentQuestion({
      id: generateId(),
      questionText: "",
      options: [
        { id: generateId(), optionText: "" },
        { id: generateId(), optionText: "" },
        { id: generateId(), optionText: "" },
        { id: generateId(), optionText: "" },
      ],
      correctOptionId: "",
      points: 1,
      explanation: "",
    });
    setEditingIndex(null);
  };

  const handleEditQuestion = (index: number) => {
    setCurrentQuestion({ ...formData.questions[index] });
    setEditingIndex(index);
  };

  const handleDeleteQuestion = (index: number) => {
    if (confirm("Delete this question?")) {
      setFormData((prev) => ({
        ...prev,
        questions: prev.questions.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSaveQuestion = () => {
    if (!currentQuestion) return;

    if (!currentQuestion.questionText.trim()) {
      showError("Question text is required");
      return;
    }

    const validOptions = currentQuestion.options.filter((o) =>
      o.optionText.trim(),
    );
    if (validOptions.length < 2) {
      showError("At least 2 options are required");
      return;
    }

    if (!currentQuestion.correctOptionId) {
      showError("Please select the correct answer");
      return;
    }

    const cleanedQuestion = {
      ...currentQuestion,
      options: validOptions,
    };

    setFormData((prev) => {
      if (editingIndex !== null) {
        const updated = [...prev.questions];
        updated[editingIndex] = cleanedQuestion;
        return { ...prev, questions: updated };
      } else {
        return { ...prev, questions: [...prev.questions, cleanedQuestion] };
      }
    });

    setCurrentQuestion(null);
    setEditingIndex(null);
    showSuccess(
      editingIndex !== null ? "Question updated!" : "Question added!",
    );
  };

  const handleSubmit = async () => {
    if (!formData.courseId) {
      showError("Please select a course");
      return;
    }

    if (formData.questions.length === 0) {
      showError("Please add at least one question");
      return;
    }

    setSaving(true);
    try {
      const questionsForApi = formData.questions.map((q, idx) => ({
        id: q.id,
        questionText: q.questionText,
        options: q.options,
        correctOptionId: q.correctOptionId,
        points: q.points,
        explanation: q.explanation,
        order: idx + 1,
      }));

      const examData = {
        courseId: formData.courseId,
        title: formData.title,
        description: formData.description,
        passingScore: formData.passingScore,
        timeLimit: formData.timeLimit,
        examDuration: formData.examDuration,
        attemptsAllowed: formData.attemptsAllowed,
        randomizeQuestions: formData.randomizeQuestions,
        showAnswersImmediately: formData.showAnswersImmediately,
        requireProctoring: formData.requireProctoring,
        isActive: formData.isActive,
        questions: questionsForApi,
      };

      let response;
      if (existingExam) {
        response = await certificateExamApi.updateCertificateExam(
          existingExam._id,
          examData,
        );
      } else {
        response = await certificateExamApi.createCertificateExam(examData);
      }

      if (response.success) {
        showSuccess(
          existingExam
            ? "Exam updated successfully!"
            : "Exam created successfully!",
        );
        navigate(`/dashboard/instructor/courses/${formData.courseId}`);
      } else {
        throw new Error(response.message || "Failed to save exam");
      }
    } catch (err: any) {
      console.error("Failed to save exam:", err);
      showError(err.message || "Failed to save exam");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "details", label: "Details", icon: FileText },
    {
      id: "questions",
      label: "Questions",
      icon: HelpCircle,
      count: formData.questions.length,
    },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 flex items-center justify-center">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
        <div className="px-4 py-3 md:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link
                to={`/dashboard/instructor/courses${courseId ? `/${courseId}` : ""}`}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <div>
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 shadow-sm">
                    <Award className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h1 className="text-sm font-semibold text-gray-900 md:text-base">
                    {existingExam
                      ? "Edit Certificate Exam"
                      : "Create Certificate Exam"}
                  </h1>
                </div>
                <p className="text-[9px] text-gray-500 ml-8 hidden sm:block">
                  {existingExam
                    ? "Modify exam details and questions"
                    : "Create a new certificate exam for your course"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="hidden md:flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-50"
              >
                {saving ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5" />
                )}
                {saving ? "Saving..." : "Save Exam"}
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Responsive */}
        <div className="border-b border-gray-100 overflow-x-auto">
          <div className="flex px-4 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[10px] md:text-[11px] font-medium transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? "text-orange-600 border-orange-500"
                    : "text-gray-500 border-transparent hover:text-gray-700"
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 text-[8px] bg-gray-100 text-gray-600 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 py-4 md:px-6 md:py-6">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Main Content */}
          <div className="flex-1">
            <AnimatePresence mode="wait">
              {activeTab === "details" && (
                <motion.div
                  key="details"
                  {...fadeInUp}
                  className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-blue-50 rounded-lg">
                      <FileText className="w-4 h-4 text-orange-500" />
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      Exam Details
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-[11px] font-medium text-gray-700 mb-1">
                        Select Course *
                      </label>
                      <select
                        value={formData.courseId}
                        onChange={(e) =>
                          setFormData({ ...formData, courseId: e.target.value })
                        }
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                        disabled={!!courseId}
                      >
                        <option value="">-- Select a course --</option>
                        {courses.map((course) => (
                          <option key={course._id} value={course._id}>
                            {course.title}
                          </option>
                        ))}
                      </select>
                      {courses.length === 0 && (
                        <p className="text-[10px] text-amber-600 mt-1">
                          No courses found. Please create a course first.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-gray-700 mb-1">
                        Exam Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-1">
                          Passing Score (%) *
                        </label>
                        <input
                          type="number"
                          value={formData.passingScore}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              passingScore: parseInt(e.target.value) || 70,
                            })
                          }
                          min="0"
                          max="100"
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-1">
                          Exam Duration (min) *
                        </label>
                        <input
                          type="number"
                          value={formData.examDuration}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              examDuration: parseInt(e.target.value) || 120,
                            })
                          }
                          min="1"
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
                        placeholder="Describe what this exam covers..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-1">
                          Time Limit (min)
                        </label>
                        <input
                          type="number"
                          value={formData.timeLimit}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              timeLimit: parseInt(e.target.value) || 60,
                            })
                          }
                          min="1"
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-700 mb-1">
                          Attempts Allowed *
                        </label>
                        <input
                          type="number"
                          value={formData.attemptsAllowed}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              attemptsAllowed: parseInt(e.target.value) || 3,
                            })
                          }
                          min="1"
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === "questions" && (
                <motion.div key="questions" {...fadeInUp} className="space-y-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-50 rounded-lg">
                          <HelpCircle className="w-4 h-4 text-purple-600" />
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900">
                          Questions ({formData.questions.length})
                        </h2>
                      </div>
                      <button
                        onClick={handleAddQuestion}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Add Question
                      </button>
                    </div>

                    {formData.questions.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                          <HelpCircle className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500">
                          No questions added yet
                        </p>
                        <button
                          onClick={handleAddQuestion}
                          className="mt-2 text-[11px] text-orange-600 hover:text-orange-700"
                        >
                          Add your first question
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {formData.questions.map((q, index) => (
                          <div
                            key={q.id}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 text-[10px] font-medium flex items-center justify-center flex-shrink-0">
                                    {index + 1}
                                  </span>
                                  <p className="text-xs font-medium text-gray-900 truncate">
                                    {q.questionText || "Untitled Question"}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-gray-500 ml-6">
                                  <span>{q.options.length} options</span>
                                  <span>•</span>
                                  <span>
                                    {q.points} pt{q.points !== 1 ? "s" : ""}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-0.5 flex-shrink-0">
                                <button
                                  onClick={() => handleEditQuestion(index)}
                                  className="p-1.5 text-gray-400 hover:text-orange-600 rounded-lg hover:bg-white transition-colors"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(index)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-white transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Question Editor Modal - Fully Responsive */}
                  <AnimatePresence>
                    {currentQuestion && (
                      <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl border-t border-gray-200 max-h-[90vh] overflow-y-auto"
                      >
                        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-green-50 rounded-lg">
                              <HelpCircle className="w-4 h-4 text-green-600" />
                            </div>
                            <h2 className="text-sm font-semibold text-gray-900">
                              {editingIndex !== null
                                ? "Edit Question"
                                : "New Question"}
                            </h2>
                          </div>
                          <button
                            onClick={() => setCurrentQuestion(null)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="p-4 space-y-4">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-1">
                              Question Text *
                            </label>
                            <textarea
                              value={currentQuestion.questionText}
                              onChange={(e) =>
                                setCurrentQuestion({
                                  ...currentQuestion,
                                  questionText: e.target.value,
                                })
                              }
                              rows={3}
                              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 resize-none"
                              placeholder="Enter your question here..."
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] font-medium text-gray-700 mb-2">
                              Options (select correct answer) *
                            </label>
                            <div className="space-y-2">
                              {currentQuestion.options.map((option, idx) => (
                                <div
                                  key={option.id}
                                  className="flex flex-col sm:flex-row sm:items-center gap-2"
                                >
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setCurrentQuestion({
                                          ...currentQuestion,
                                          correctOptionId: option.id,
                                        })
                                      }
                                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                        currentQuestion.correctOptionId ===
                                        option.id
                                          ? "border-green-500 bg-green-500"
                                          : "border-gray-300"
                                      }`}
                                    >
                                      {currentQuestion.correctOptionId ===
                                        option.id && (
                                        <CheckCircle className="w-3 h-3 text-white" />
                                      )}
                                    </button>
                                    <span className="text-[10px] text-gray-500 sm:hidden">
                                      Option {idx + 1}
                                    </span>
                                  </div>
                                  <input
                                    type="text"
                                    value={option.optionText}
                                    onChange={(e) => {
                                      const newOptions = [
                                        ...currentQuestion.options,
                                      ];
                                      newOptions[idx] = {
                                        ...newOptions[idx],
                                        optionText: e.target.value,
                                      };
                                      setCurrentQuestion({
                                        ...currentQuestion,
                                        options: newOptions,
                                      });
                                    }}
                                    className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                    placeholder={`Option ${idx + 1}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                                Points *
                              </label>
                              <input
                                type="number"
                                value={currentQuestion.points}
                                onChange={(e) =>
                                  setCurrentQuestion({
                                    ...currentQuestion,
                                    points: parseInt(e.target.value) || 1,
                                  })
                                }
                                min="1"
                                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                                Explanation (Optional)
                              </label>
                              <input
                                type="text"
                                value={currentQuestion.explanation || ""}
                                onChange={(e) =>
                                  setCurrentQuestion({
                                    ...currentQuestion,
                                    explanation: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                                placeholder="Explanation for correct answer"
                              />
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2 pt-2">
                            <button
                              onClick={handleSaveQuestion}
                              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
                            >
                              <Save className="w-3.5 h-3.5" />
                              Save Question
                            </button>
                            <button
                              onClick={() => setCurrentQuestion(null)}
                              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}

              {activeTab === "settings" && (
                <motion.div
                  key="settings"
                  {...fadeInUp}
                  className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-orange-50 rounded-lg">
                      <Settings className="w-4 h-4 text-orange-600" />
                    </div>
                    <h2 className="text-sm font-semibold text-gray-900">
                      Exam Settings
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {[
                      {
                        key: "randomizeQuestions",
                        label: "Randomize Questions",
                        description: "Shuffle question order for each student",
                      },
                      {
                        key: "showAnswersImmediately",
                        label: "Show Answers After Submission",
                        description:
                          "Students see correct answers immediately after submission",
                      },
                      {
                        key: "requireProctoring",
                        label: "Require Proctoring",
                        description:
                          "Monitor student activity during exam (coming soon)",
                      },
                      {
                        key: "isActive",
                        label: "Exam is Active",
                        description: "Students can take this exam",
                      },
                    ].map((setting) => (
                      <label
                        key={setting.key}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData[
                              setting.key as keyof ExamFormData
                            ] as boolean
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [setting.key]: e.target.checked,
                            })
                          }
                          disabled={setting.key === "requireProctoring"}
                          className="mt-0.5 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500/20"
                        />
                        <div className="flex-1">
                          <span className="block text-xs font-medium text-gray-900">
                            {setting.label}
                          </span>
                          <span className="block text-[10px] text-gray-500">
                            {setting.description}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar - Preview */}
          {(showPreview || !isMobile) && (
            <div className="lg:w-80 space-y-4">
              {/* Preview Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <h3 className="text-xs font-semibold text-gray-900">
                    Exam Preview
                  </h3>
                </div>
                <div className="space-y-2">
                  {[
                    {
                      label: "Title",
                      value: formData.title || "Not set",
                      icon: FileText,
                    },
                    {
                      label: "Questions",
                      value: formData.questions.length,
                      icon: HelpCircle,
                    },
                    {
                      label: "Passing Score",
                      value: `${formData.passingScore}%`,
                      icon: TrendingUp,
                    },
                    {
                      label: "Duration",
                      value: `${formData.examDuration} min`,
                      icon: Timer,
                    },
                    {
                      label: "Attempts",
                      value: formData.attemptsAllowed,
                      icon: Shield,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-600">
                        <item.icon className="w-3 h-3" />
                        {item.label}
                      </div>
                      <span className="text-[11px] font-medium text-gray-900">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips Card */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-amber-600" />
                  <h3 className="text-xs font-semibold text-gray-900">
                    Pro Tips
                  </h3>
                </div>
                <ul className="space-y-2 text-[11px] text-gray-700">
                  <li className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    Add 10-20 questions for a comprehensive exam
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    Mark the correct answer for each question
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    Set passing score to 70% (recommended)
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    Students need 100% course progress to take exam
                  </li>
                  <li className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                    Provide explanations to help students learn
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
